import { useState } from 'react';
import { PackageCheck } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCancelUncollectedMutation, useCollectSaleMutation, useUncollectedSalesQuery,
  type UncollectedSale,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal, PageHeader,
  Pagination, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { PAYMENT_TONE } from './SalesPage';

/**
 * Goods that were paid for and left behind.
 *
 * They left stock when they were sold — they are the customer's, not the
 * shop's to sell again — so this is the only place they show up. Each one stays
 * here until it is handed over, oldest first, because the goods that have
 * waited longest are the ones someone should chase.
 */
export function UncollectedPage() {
  const format = useFormat();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [handing, setHanding] = useState<UncollectedSale | null>(null);
  const [cancelling, setCancelling] = useState<UncollectedSale | null>(null);

  const { data, isLoading, isError, error, refetch } = useUncollectedSalesQuery({
    page,
    limit: 20,
    search: search || undefined,
  });

  return (
    <>
      <PageHeader
        title="To collect"
        description="Paid for, not yet taken. These have already left stock — they belong to the customer."
      />

      <Card>
        <div className="border-b border-subtle p-4">
          <div className="w-full sm:w-72">
            <Input
              label="Find"
              placeholder="Sale number or customer"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            icon={PackageCheck}
            title="Nothing waiting to be collected"
            description='Tick "Collect later" on a new sale when they pay now and come back for the goods.'
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Sale</Th>
                  <Th>Customer</Th>
                  <Th>Waiting</Th>
                  <Th>Payment</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((sale) => (
                  <tr key={sale.id}>
                    <Td>
                      <p className="font-medium">{sale.number}</p>
                      <p className="text-xs text-muted">Sold {formatDate(sale.issuedAt, format)}</p>
                    </Td>
                    <Td>
                      <p>{sale.customer?.name ?? 'Walk-in'}</p>
                      {sale.customer?.phone && <p className="text-xs text-muted">{sale.customer.phone}</p>}
                    </Td>
                    <Td>
                      <ul className="text-sm">
                        {sale.items.map((item) => (
                          <li key={item.id}>
                            {item.description} × {formatQuantity(item.waiting)}
                            {Number(item.collected) > 0 && (
                              <span className="text-xs text-muted"> ({formatQuantity(item.collected)} taken)</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </Td>
                    <Td>
                      <Badge tone={PAYMENT_TONE[sale.paymentStatus]}>
                        {sale.paymentStatus.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                      {Number(sale.dueAmount) > 0 && (
                        <p className="mt-1 text-xs text-red-600">{formatMoney(sale.dueAmount, format)} still owed</p>
                      )}
                    </Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Can permission={P.SALE_REFUND}>
                          <Button size="sm" variant="ghost" onClick={() => setCancelling(sale)}>
                            Cancel
                          </Button>
                        </Can>
                        <Can permission={P.SALE_CREATE}>
                          <Button size="sm" variant="outline" onClick={() => setHanding(sale)}>
                            Hand over
                          </Button>
                        </Can>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      {handing && <HandOver sale={handing} onClose={() => setHanding(null)} />}
      {cancelling && <CancelOrder sale={cancelling} onClose={() => setCancelling(null)} />}
    </>
  );
}

/** Records what the customer took today — all of it, or part when they come in more than one trip. */
function HandOver({ sale, onClose }: { sale: UncollectedSale; onClose: () => void }) {
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(sale.items.map((item) => [item.id, item.waiting])),
  );
  const [notes, setNotes] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [collect, state] = useCollectSaleMutation();

  const lines = sale.items
    .map((item) => ({ saleItemId: item.id, quantity: Number(quantities[item.id] || 0) }))
    .filter((line) => line.quantity > 0);
  const tooMany = sale.items.some((item) => Number(quantities[item.id] || 0) > Number(item.waiting));

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await collect({ id: sale.id, lines, notes: notes || undefined }).unwrap();
      onClose();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'The handover could not be recorded'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Hand over ${sale.number}`}
      description={`To ${sale.customer?.name ?? 'the walk-in customer'}. Change a quantity if they are taking only part today.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => void submit()}
            loading={state.isLoading}
            disabled={lines.length === 0 || tooMany}
          >
            Record handover
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {failure && <Alert tone="danger">{failure}</Alert>}
        {sale.items.map((item) => (
          <div key={item.id} className="flex items-end gap-3">
            <p className="flex-1 pb-2.5 text-sm">{item.description}</p>
            <div className="w-32">
              <Input
                label="Taking now"
                type="number"
                min="0"
                max={item.waiting}
                value={quantities[item.id] ?? ''}
                onChange={(event) => setQuantities({ ...quantities, [item.id]: event.target.value })}
                error={Number(quantities[item.id] || 0) > Number(item.waiting) ? `Only ${formatQuantity(item.waiting)}` : undefined}
                hint={`${formatQuantity(item.waiting)} waiting`}
              />
            </div>
          </div>
        ))}
        <Input
          label="Note"
          placeholder="e.g. collected by the driver, Musa"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * The customer no longer wants what they left behind. The goods go back on
 * the shelf as a return, and the money goes back to them now or stays on their
 * account — asked each time, because it is their choice at the counter.
 */
function CancelOrder({ sale, onClose }: { sale: UncollectedSale; onClose: () => void }) {
  const format = useFormat();
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(sale.items.map((item) => [item.id, item.waiting])),
  );
  // A walk-in has no account to hold credit, so for them it can only be cash.
  const [refund, setRefund] = useState<'CASH' | 'CREDIT'>('CASH');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [cancel, state] = useCancelUncollectedMutation();

  const lines = sale.items
    .map((item) => ({ saleItemId: item.id, quantity: Number(quantities[item.id] || 0) }))
    .filter((line) => line.quantity > 0);
  const tooMany = sale.items.some((item) => Number(quantities[item.id] || 0) > Number(item.waiting));

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await cancel({ saleId: sale.id, refund, lines, reason: reason || undefined }).unwrap();
      onClose();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'The order could not be cancelled'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Cancel ${sale.number}`}
      description="The goods go back into stock as a return. Lower a quantity to cancel only part of it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Keep the order</Button>
          <Button
            variant="danger"
            onClick={() => void submit()}
            loading={state.isLoading}
            disabled={lines.length === 0 || tooMany}
          >
            Cancel and restock
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {failure && <Alert tone="danger">{failure}</Alert>}

        <div className="space-y-3">
          {sale.items.map((item) => (
            <div key={item.id} className="flex items-end gap-3">
              <p className="flex-1 pb-2.5 text-sm">{item.description}</p>
              <div className="w-32">
                <Input
                  label="Back to stock"
                  type="number"
                  min="0"
                  max={item.waiting}
                  value={quantities[item.id] ?? ''}
                  onChange={(event) => setQuantities({ ...quantities, [item.id]: event.target.value })}
                  error={Number(quantities[item.id] || 0) > Number(item.waiting) ? `Only ${formatQuantity(item.waiting)}` : undefined}
                  hint={`${formatQuantity(item.waiting)} waiting`}
                />
              </div>
            </div>
          ))}
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">The money</legend>
          <label className="flex items-start gap-3 rounded-lg border border-subtle p-3">
            <input
              type="radio"
              name="refund"
              className="mt-1"
              checked={refund === 'CASH'}
              onChange={() => setRefund('CASH')}
            />
            <span>
              <span className="block text-sm font-medium">Give the money back now</span>
              <span className="block text-xs text-muted">
                It comes out of the account the sale was paid into.
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-3 rounded-lg border border-subtle p-3 ${sale.customer ? '' : 'opacity-50'}`}
          >
            <input
              type="radio"
              name="refund"
              className="mt-1"
              disabled={!sale.customer}
              checked={refund === 'CREDIT'}
              onChange={() => setRefund('CREDIT')}
            />
            <span>
              <span className="block text-sm font-medium">Keep it as credit</span>
              <span className="block text-xs text-muted">
                {sale.customer
                  ? `${sale.customer.name} can spend it later with Store credit. It shows under Deposits.`
                  : 'Not for a walk-in: there is no customer account to hold it.'}
              </span>
            </span>
          </label>
        </fieldset>

        <Input
          label="Reason"
          placeholder="e.g. customer changed their mind"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        {Number(sale.dueAmount) > 0 && (
          <Alert tone="info">
            {formatMoney(sale.dueAmount, format)} is still owed on this sale. The value of the goods
            taken back clears that first; only money actually paid can be given back.
          </Alert>
        )}
      </div>
    </Modal>
  );
}
